/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1696464000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Oct 05 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "oracle",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Compatible versions",
          "depth": 1,
          "id": "compatible-versions"
        }, {
          "value": "Unsupported data types",
          "depth": 1,
          "id": "unsupported-data-types"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      em: "em",
      code: "code"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, SourceTestSnippet, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    if (!SourceTestSnippet) _missingMdxReference("SourceTestSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in your Oracle database and push it to downstream destinations. Most of the setup occurs in the Hightouch UI, but you need access to your Oracle instance for information like your host, port, database name, and credentials."
      }), "\n", _jsx(WhitelistDetails, {}), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Oracle DB"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "Oracle"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Oracle server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Oracle server. The default port number is 1521, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": This specifies the database to use when Hightouch executes queries in Oracle DB. This is different than the host, but your host address might contain your database name."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": The user that has access through Hightouch to the database and tables you want to pull from. It's best to ", _jsx(_components.a, {
            href: "https://docs.oracle.com/en/database/oracle/oracle-database/19/sqlrf/CREATE-USER.html",
            children: "create a new user"
          }), " specifically for Hightouch access. ", _jsx(_components.em, {
            children: "Do not use the root user."
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the specified user."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsxs(_components.p, {
        children: ["For optimal performance, Hightouch ", _jsx(_components.a, {
          href: "/getting-started/concepts#change-data-capture-cdc",
          children: "tracks incremental changes"
        }), "\nin your data model—such as added, changed, or removed rows—and only syncs those records.\nYou can choose between two different sync engines for this work."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Basic engine"
        }), " requires read-only access to Oracle. Hightouch executes a query in your database, reads all query results, and then determines incremental changes using Hightouch's infrastructure. This engine is easier to set up since it requires read—not write—access to Oracle."]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsx(SourceTestSnippet, {
        source: "Oracle"
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Oracle."]
      }), "\n", _jsx(_components.p, {
        children: "The Oracle source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "compatible-versions",
        children: "Compatible versions"
      }), "\n", _jsxs(_components.p, {
        children: ["The supported Oracle DB version is ", _jsx(_components.code, {
          children: "19c"
        }), ". Other versions aren't officially supported but may work regardless."]
      }), "\n", _jsx(_components.h3, {
        id: "unsupported-data-types",
        children: "Unsupported data types"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch does not support ", _jsx(_components.code, {
          children: "INTERVAL"
        }), " types for Oracle."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
